// Constants recovered from CAXeng.dll (notes/CAXeng.md). Section references point to that file.
import type { SsMethod, TaxYear } from '@divorceos/schema';

/** §2.2 — per-number-of-children multiplier (index = min(nkids, 10)); DAT_10025860. */
export const K_MULTIPLIERS: readonly number[] = [0, 1.0, 1.6, 2.0, 2.3, 2.5, 2.625, 2.75, 2.813, 2.844, 2.86];

/** §2.6 — adjusted-nets iteration tolerance (DAT_10020760) and cap. */
export const ADJUSTED_NETS_TOLERANCE = 0.25;
export const ADJUSTED_NETS_MAX_ITER = 100;

/** §2.4 — FC 4071(b) hardship iteration tolerance and cap. */
export const HARDSHIP_TOLERANCE = 0.5;
export const HARDSHIP_MAX_ITER = 100;

/** §4 — tactic solver: tax change tolerance (1.0, or 0.1 once |tax| < 10) and cap. */
export const TACTIC_TAX_TOLERANCE = 1.0;
export const TACTIC_TAX_TOLERANCE_SMALL = 0.1;
export const TACTIC_MAX_ITER = 100;
export const TACTIC_DIVERGENCE_LIMIT = 999999;

/** §4 — tax-minimising SS search: coarse steps, bisection depth, derivative step. */
export const MIN_TAX_COARSE_STEPS = 50;
export const MIN_TAX_BISECTIONS = 6;
export const MIN_TAX_DERIVATIVE_H = 0.1;
export const MIN_TAX_DERIVATIVE_EPS = -1e-7;

/**
 * §2.3 — FC 4055(b)(7) low-income adjustment threshold (monthly), keyed by the tax year whose
 * March figure it is (the original indexes by calendar year − 1999). 2024 = 2056 × 1.041 (FUN_100163ee).
 * TODO(2025): the build has no 2025 figure; 2140.3 is carried forward until the published number is known.
 */
export const LOW_INCOME_THRESHOLD: Record<number, number> = {
  2017: 1644, 2018: 1692, 2019: 1755, 2020: 1807, 2021: 1837, 2022: 1915, 2023: 2056, 2024: 2140.3, 2025: 2140.3,
};
export const LOW_INCOME_THRESHOLD_FALLBACK = 2056;

/**
 * SB 343 (Stats. 2023, ch. 213; FC 4055 operative 1 Sep 2024) — California statewide minimum wage per hour under
 * Labor Code 1182.12, by calendar year (rate effective 1 January). Source: DIR Labor Commissioner, Minimum Wage FAQ
 * (https://www.dir.ca.gov/dlse/faq_minimumwage.htm): 2022 $15.00 (26+ employees; $14.00 for 25 or fewer),
 * 2023 $15.50, 2024 $16.00, 2025 $16.50, 2026 $16.90 (all employers). Add each new year when the Director of
 * Finance certifies it (on or before 1 August of the prior year).
 */
export const CA_MINIMUM_WAGE: Record<number, number> = { 2022: 15.0, 2023: 15.5, 2024: 16.0, 2025: 16.5, 2026: 16.9 };

/** FC 4055(b)(7) as amended by SB 343: full-time = 40 hours per week, 52 weeks per year, expressed monthly. */
export const SB343_FULL_TIME_HOURS_PER_YEAR = 40 * 52;

/** SB 343 operative date (FC 4055(d)): 1 September 2024 (month index 8). */
export const SB343_OPERATIVE = { year: 2024, monthIndex: 8, day: 1 } as const;

/** §2.1 — capital-loss floor applied in gross income (monthly): −250, −125 when MFS. */
export const CAP_LOSS_FLOOR = -250;
export const CAP_LOSS_FLOOR_MFS = -125;

/**
 * §3 — spousal support formula id per method: the id IS the method's own code (case+0x1b7a), confirmed against
 * the original engine (bridge selftest: Alameda 442.76 ≠ Santa Clara 393.30, Marin = Kings, Yolo = Alameda for
 * n > 0 since id 8 = id 3 when there are children, Humboldt negative → 0, San Diego = the K·G form).
 */
export const SS_FORMULA_BY_METHOD: Record<SsMethod, number> = {
  USER: 0, FORMULA: 1, SANTA_CLARA: 2, ALAMEDA: 3, MARIN: 4, KINGS: 5, SAN_DIEGO: 6, HUMBOLDT: 7, YOLO: 8,
};

/** §6.4 — value of one withholding allowance used when searching allowances (Pub 15-T, pre-2020 W-4 conversion). */
export const WITHHOLDING_ALLOWANCE_VALUE = 4300;
export const WITHHOLDING_MAX_ALLOWANCES = 20;

/**
 * Whether TANF + child support received (row 7) is treated like "income used for child support only"
 * (row 8, case+0x32c): included in the CS/SS formula nets and net spendable, excluded from the displayed
 * net disposable income. The task brief asks for this; the original's gross-income list (§2.1) only
 * shows 0x32c, so flip this if the demo turns out to exclude TANF/CS from the formula nets.
 */
export const TANF_CS_IN_FORMULA_NET = true;

export const MAX_CHILDREN = 10;

export const SUPPORTED_YEARS: TaxYear[] = [2022, 2023, 2024, 2025, 2026, 2027];
